import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Filter, Search, X, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIFFICULTY_OPTIONS = ['all', 'beginner', 'intermediate', 'advanced'];

const readingTime = (text = '') => {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const Education = () => {
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await axios.get(`${API}/articles`);
        setArticles(res.data);
      } catch (e) {
        console.error('Failed to fetch articles', e);
      } finally {
        setArticlesLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const availableCategories = useMemo(() => {
    const set = new Set(articles.map((a) => a.category));
    return ['all', ...Array.from(set)];
  }, [articles]);

  const filteredArticles = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((a) => {
      if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
      if (difficultyFilter !== 'all' && a.difficulty_level !== difficultyFilter) return false;
      if (!kw) return true;
      return (
        a.title.toLowerCase().includes(kw) ||
        a.excerpt.toLowerCase().includes(kw) ||
        a.content.toLowerCase().includes(kw) ||
        a.category.toLowerCase().includes(kw)
      );
    });
  }, [articles, keyword, categoryFilter, difficultyFilter]);

  const clearFilters = () => {
    setKeyword('');
    setCategoryFilter('all');
    setDifficultyFilter('all');
  };

  const hasActiveFilters = keyword || categoryFilter !== 'all' || difficultyFilter !== 'all';

  const categories = [
    {
      title: 'Mutual Fund Basics',
      topics: [
        'What is a mutual fund?',
        'How mutual funds work',
        'NAV explained',
        'Direct and regular plans',
        'Growth and dividend options',
        'Role of the fund manager',
        'Expense ratio',
        'Exit load'
      ]
    },
    {
      title: 'Types of Mutual Funds',
      topics: [
        'Large-cap funds',
        'Mid-cap funds',
        'Small-cap funds',
        'Flexi-cap funds',
        'Multi-cap funds',
        'Debt funds',
        'Hybrid funds',
        'Index funds'
      ]
    },
    {
      title: 'Investment Methods',
      topics: [
        'SIP (Systematic Investment Plan)',
        'Lump-sum investment',
        'STP (Systematic Transfer Plan)',
        'SWP (Systematic Withdrawal Plan)',
        'Portfolio rebalancing',
        'Goal-based investing'
      ]
    },
    {
      title: 'Risk and Return',
      topics: [
        'Understanding market risk',
        'Credit risk',
        'Interest-rate risk',
        'Liquidity risk',
        'Volatility',
        'Investment horizon',
        'Absolute return vs CAGR vs XIRR'
      ]
    },
    {
      title: 'Taxation',
      topics: [
        'Equity fund taxation',
        'Debt fund taxation',
        'Capital gains',
        'Dividend taxation',
        'SIP withdrawal taxation',
        'SWP taxation'
      ]
    },
    {
      title: 'Investor Behaviour',
      topics: [
        'Importance of staying invested',
        'Risks of market timing',
        'Power of compounding',
        'Effects of panic selling',
        'Importance of diversification',
        'Why portfolio reviews matter'
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="education-page">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="education-heading">
            Investor Education Centre
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-3xl mx-auto leading-relaxed">
            Empower yourself with knowledge. Learn about mutual funds, investment strategies, risk management, and more to make informed financial decisions.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="articles-browse-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-3">
              Browse Articles
            </h2>
            <p className="text-base font-body text-[#4A4A4A]">
              Search by keyword, or filter by category and difficulty to find the article that fits your learning stage.
            </p>
          </div>

          <div className="bg-[#FDF8F5] border border-[#E5E5E5] rounded-2xl p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative md:col-span-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="article-search-input"
                />
              </div>
              <div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body"
                  data-testid="article-category-filter"
                >
                  {availableCategories.map((c) => (
                    <option key={c} value={c}>
                      {c === 'all' ? 'All Categories' : c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  value={difficultyFilter}
                  onChange={(e) => setDifficultyFilter(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body capitalize"
                  data-testid="article-difficulty-filter"
                >
                  {DIFFICULTY_OPTIONS.map((d) => (
                    <option key={d} value={d} className="capitalize">
                      {d === 'all' ? 'All Levels' : d}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E5E5]">
                <p className="text-sm font-body text-[#4A4A4A]" data-testid="article-results-count">
                  Showing <span className="font-semibold text-[#A0451E]">{filteredArticles.length}</span> of {articles.length} articles
                </p>
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-sm font-body text-[#A0451E] hover:text-[#D97706] transition"
                  data-testid="article-clear-filters"
                >
                  <X className="w-4 h-4" />
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {articlesLoading ? (
            <div className="text-center py-12 text-sm font-body text-[#737373]">Loading articles...</div>
          ) : filteredArticles.length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center" data-testid="article-empty-state">
              <BookOpen className="w-10 h-10 text-[#D97706] mx-auto mb-4" />
              <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-2">
                {articles.length === 0 ? 'Articles coming soon' : 'No articles match your filters'}
              </h3>
              <p className="text-sm font-body text-[#4A4A4A]">
                {articles.length === 0
                  ? 'Our team is preparing investor-education content. Please check back shortly.'
                  : 'Try a different keyword or reset the category and difficulty filters.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
              {filteredArticles.map((article, index) => (
                <article
                  key={article.article_id}
                  className="bg-white rounded-2xl p-6 border border-[#E5E5E5] card-hover cursor-pointer flex flex-col"
                  onClick={() => navigate(`/education/${article.slug}`)}
                  data-testid={`article-card-${index}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block bg-[#D97706] text-white text-xs px-3 py-1 rounded-full font-body">
                      {article.category}
                    </span>
                    <span className="inline-block bg-[#FDF8F5] text-[#A0451E] text-xs px-3 py-1 rounded-full font-body border border-[#E5E5E5] capitalize">
                      {article.difficulty_level}
                    </span>
                  </div>
                  <h3 className="text-lg font-heading font-medium text-[#A0451E] mb-3 leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-sm font-body text-[#4A4A4A] leading-relaxed line-clamp-3 flex-grow">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex items-center justify-between text-sm font-body">
                    <span className="flex items-center gap-1.5 text-[#737373]" data-testid={`article-reading-time-${index}`}>
                      <Clock className="w-3.5 h-3.5" />
                      {readingTime(article.content)} min read
                    </span>
                    <span className="font-semibold text-[#D97706]">Read more &rarr;</span>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E]">
                Browse by Category
              </h2>
              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-4 py-2 border border-[#E5E5E5] rounded-full text-sm font-body text-[#4A4A4A] hover:border-[#A0451E] transition">
                  <Filter className="w-4 h-4" />
                  All Topics
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <div
                key={index}
                className="bg-[#FDF8F5] rounded-2xl p-8 border border-[#E5E5E5] card-hover"
                data-testid={`education-category-${index}`}
              >
                <div className="flex items-center mb-6">
                  <BookOpen className="w-6 h-6 text-[#D97706] mr-3" />
                  <h3 className="text-xl font-heading font-medium text-[#A0451E]">
                    {category.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {category.topics.map((topic, tIndex) => (
                    <li
                      key={tIndex}
                      className="flex items-start text-sm font-body text-[#4A4A4A] hover:text-[#D97706] cursor-pointer transition"
                    >
                      <span className="w-1.5 h-1.5 bg-[#D97706] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 section-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#A0451E] rounded-2xl p-12 text-center text-white">
            <h2 className="text-2xl sm:text-3xl font-heading font-medium mb-6">
              Important Disclaimer
            </h2>
            <p className="text-base font-body max-w-3xl mx-auto leading-relaxed mb-4">
              The educational content provided here is for general informational purposes only. It should not be treated as personalised investment, legal, or tax advice.
            </p>
            <p className="text-sm font-body text-white/80 max-w-2xl mx-auto">
              Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">
            Need Help With Mutual Fund Investing?
          </h2>
          <p className="text-base font-body text-[#4A4A4A] mb-8 leading-relaxed">
            Our team can share mutual fund scheme information and assist you with your investments. Schedule a consultation with our team.
          </p>
          <button
            onClick={() => navigate('/appointment')}
            className="btn-primary"
            data-testid="education-book-appointment-btn"
          >
            Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default Education;